import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Eye, Search, MapPin, Building2, Upload, Filter, ChevronDown, ChevronUp, QrCode, AlertTriangle } from 'lucide-react';
import Table, { ActionButton, ActionMenu, StatusBadge } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

/**
 * Watersheds Management Page
 * 
 * Comprehensive watershed management interface for administrators.
 * Allows viewing, creating, updating, and deleting watershed records.
 */
const WatershedsPage = () => {
  const { user } = useAuth();
  const [watersheds, setWatersheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    name: '',
    country: '',
    owner: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedWatershed, setSelectedFarm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Report modal data
  const [reportWatershedData, setReportFarmData] = useState(null);

  // CSV Upload states
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // QR Code generation state
  const [qrCodeGenerating, setQrCodeGenerating] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    state: '',
    address: '',
    description: '',
    area: '',
    coordinates: {
      latitude: '',
      longitude: '',
    },
  });

  const columns = [
    {
      key: 'name',
      title: 'Watershed Name',
      sortable: true,
      render: (watershed) => (
        <div className="flex items-center">
          <div className="bg-primary-100 p-2 rounded-lg mr-3">
            <Building2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-medium text-secondary-900">{watershed.name}</div>
            <div className="text-sm text-secondary-500 flex items-center">
              <MapPin className="w-3 h-3 mr-1" />
              {watershed.address || watershed.country}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'created_by',
      title: 'Created By',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (watershed) => (
        <div>
          <div className="font-medium text-secondary-900">{watershed.created_by || 'System'}</div>
        </div>
      ),
    },
    {
      key: 'country',
      title: 'Country',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (watershed) => (
        <span className="text-secondary-900">
          {watershed.country || 'Not specified'}
        </span>
      ),
    },
    {
      key: 'state',
      title: 'State',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (watershed) => (
        <span className="text-secondary-900">
          {watershed.state || watershed.region || 'Not specified'}
        </span>
      ),
    },
    {
      key: 'area',
      title: 'Area',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (watershed) => (
        <span className="text-secondary-900">
          {watershed.area ? `${watershed.area}` : 'Not specified'}
        </span>
      ),
    },
    {
      key: 'qr_code',
      title: 'QR Code',
      className: 'hidden lg:table-cell',
      render: (watershed) => (
        watershed.qr_code ? (
          <div className="flex items-center">
            <QrCode className="w-4 h-4 text-primary-600 mr-1" />
            <a
              href={watershed.qr_code}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              View QR
            </a>
          </div>
        ) : (
          <button
            onClick={() => handleGenerateQRCode(watershed.id)}
            disabled={qrCodeGenerating[watershed.id]}
            className="btn btn-sm btn-secondary text-xs px-2 py-1 disabled:opacity-50"
          >
            {qrCodeGenerating[watershed.id] ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600 mr-1"></div>
                Generating...
              </>
            ) : (
              <>
                <QrCode className="w-3 h-3 mr-1" />
                Generate QR
              </>
            )}
          </button>
        )
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (watershed) => new Date(watershed.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (watershed) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewFarm(watershed)}
          />
          <ActionMenu
            item={watershed}
            actions={[
              {
                key: 'report',
                label: 'Submit Report',
                icon: AlertTriangle,
                variant: 'warning',
                onClick: handleReportFarm,
              },
              {
                key: 'edit',
                label: 'Edit Watershed',
                icon: Edit,
                onClick: handleEditFarm,
              },
              {
                key: 'delete',
                label: 'Delete Watershed',
                icon: Trash2,
                variant: 'danger',
                onClick: handleDeleteFarm,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadFarms();
  }, [filters, sortConfig]);

  const loadFarms = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getWatersheds(params);
      
      if (response.success) {
        setWatersheds(response.data.watersheds || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load watersheds');
      }
    } catch (error) {
      console.error('Error loading watersheds:', error);
      toast.error('Failed to load watersheds');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleNameFilter = (value) => {
    setFilters(prev => ({ ...prev, name: value, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewFarm = (watershed) => {
    setSelectedFarm(watershed);
    setShowViewModal(true);
  };

  const handleEditFarm = (watershed) => {
    setSelectedFarm(watershed);
    setFormData({
      name: watershed.name || '',
      country: watershed.country || '',
      state: watershed.state || '',
      address: watershed.address || '',
      description: watershed.description || '',
      area: watershed.area ? watershed.area.toString() : '',
      coordinates: {
        latitude: watershed.coordinates?.latitude?.toString() || '',
        longitude: watershed.coordinates?.longitude?.toString() || '',
      },
    });
    setShowEditModal(true);
  };

  const handleDeleteFarm = (watershed) => {
    setSelectedFarm(watershed);
    setShowDeleteModal(true);
  };

  const handleReportFarm = async (watershed) => {
    try {
      // Get the full watershed data
      const watershedResponse = await apiService.getWatershed(watershed.id);
      if (!watershedResponse.success) {
        toast.error('Failed to load watershed details');
        return;
      }

      // Structure the data as expected by ReportForm (watershed-level report)
      const watershedData = {
        watershed: watershedResponse.data
      };

      setReportFarmData(watershedData);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error loading watershed data for report:', error);
      toast.error('Failed to load watershed details for reporting');
    }
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    setReportFarmData(null);
    toast.success('Report submitted successfully');
  };

  const handleCreateFarm = () => {
    setSelectedFarm(null);
    setFormData({
      name: '',
      country: '',
      state: '',
      address: '',
      description: '',
      area: '',
      coordinates: {
        latitude: '',
        longitude: '',
      },
    });
    setShowCreateModal(true);
  };

  const handleFormSubmit = async () => {
    try {
      setFormLoading(true);
      
      // Validate and convert area to number
      let areaValue = null;
      if (formData.area && formData.area !== '') {
        const parsedArea = parseFloat(formData.area);
        if (isNaN(parsedArea) || parsedArea < 0) {
          toast.error('Please enter a valid area value');
          setFormLoading(false);
          return;
        }
        areaValue = parsedArea;
      }
      
      // Process coordinates
      let coordinatesValue = null;
      if (formData.coordinates.latitude !== '' || formData.coordinates.longitude !== '') {
        const lat = parseFloat(formData.coordinates.latitude);
        const lng = parseFloat(formData.coordinates.longitude);

        // Validate coordinates if provided
        if (formData.coordinates.latitude !== '' && (isNaN(lat) || lat < -90 || lat > 90)) {
          toast.error('Please enter a valid latitude between -90 and 90');
          setFormLoading(false);
          return;
        }

        if (formData.coordinates.longitude !== '' && (isNaN(lng) || lng < -180 || lng > 180)) {
          toast.error('Please enter a valid longitude between -180 and 180');
          setFormLoading(false);
          return;
        }

        // Only include coordinates if both values are provided
        if (formData.coordinates.latitude !== '' && formData.coordinates.longitude !== '') {
          coordinatesValue = {
            latitude: lat,
            longitude: lng
          };
        }
      }

      // Convert area to number if it exists and is not empty
      const processedFormData = {
        ...formData,
        area: areaValue,
        coordinates: coordinatesValue
      };

      let response;
      if (selectedWatershed) {
        response = await apiService.updateWatershed(selectedWatershed.id, processedFormData);
      } else {
        response = await apiService.createWatershed(processedFormData);
      }
      
      if (response.success) {
        toast.success(selectedWatershed ? 'Watershed updated successfully' : 'Watershed created successfully');
        setShowEditModal(false);
        setShowCreateModal(false);
        loadFarms();
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Operation failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWatershed) return;

    try {
      const response = await apiService.deleteWatershed(selectedWatershed.id);
      
      if (response.success) {
        toast.success('Watershed deleted successfully');
        setShowDeleteModal(false);
        loadFarms();
      } else {
        toast.error(response.message || 'Failed to delete watershed');
      }
    } catch (error) {
      console.error('Error deleting watershed:', error);
      toast.error('Failed to delete watershed');
    }
  };

  const handleInputChange = (field, value) => {
    // Handle nested coordinate fields
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
      return;
    }

    // Special handling for area field - only allow numeric values
    if (field === 'area') {
      // Allow empty string, digits, and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // CSV Upload functionality
  const handleCSVUpload = () => {
    setShowCSVUploadModal(true);
    setCsvFile(null);
  };

  const handleCSVFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        toast.error('Please select a valid CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleCSVUploadSubmit = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setCsvUploading(true);
      const response = await apiService.uploadWatershedsCSV(csvFile);

      if (response.success) {
        const { total_created, total_errors = 0, errors = [] } = response.data;

        if (total_errors === 0) {
          toast.success(`Successfully created ${total_created} watersheds from CSV`);
        } else {
          toast.success(`Created ${total_created} watersheds with ${total_errors} errors. Check console for details.`);
          console.error('CSV Upload Errors:', errors);
        }

        setShowCSVUploadModal(false);
        setCsvFile(null);
        loadFarms(); // Reload the watersheds list
      } else {
        toast.error(response.message || 'Failed to upload CSV');
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error(error.message || 'Failed to upload CSV file');
    } finally {
      setCsvUploading(false);
    }
  };

  const handleGenerateQRCode = async (watershedId) => {
    try {
      setQrCodeGenerating(prev => ({ ...prev, [watershedId]: true }));

      const response = await apiService.generateWatershedQRCode(watershedId);

      if (response.success) {
        toast.success('QR code generated successfully');
        loadFarms(); // Reload to get the updated watershed with QR code
      } else {
        toast.error(response.message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('QR code generation error:', error);
      toast.error(error.message || 'Failed to generate QR code');
    } finally {
      setQrCodeGenerating(prev => ({ ...prev, [watershedId]: false }));
    }
  };

  const renderFormFields = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Watershed Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className="form-input"
          placeholder="Enter watershed name"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Country *
          </label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="form-input"
            placeholder="Enter country"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            State
          </label>
          <input
            type="text"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className="form-input"
            placeholder="Enter state"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Address *
        </label>
        <textarea
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          rows={2}
          className="form-input"
          placeholder="Enter full address"
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Latitude
          </label>
          <input
            type="number"
            step="any"
            value={formData.coordinates.latitude}
            onChange={(e) => handleInputChange('coordinates.latitude', e.target.value)}
            className="form-input"
            placeholder="Enter latitude (e.g., 22.7704832)"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Longitude
          </label>
          <input
            type="number"
            step="any"
            value={formData.coordinates.longitude}
            onChange={(e) => handleInputChange('coordinates.longitude', e.target.value)}
            className="form-input"
            placeholder="Enter longitude (e.g., 89.5254528)"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Area (sq km)
        </label>
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]*\.?[0-9]*"
          value={formData.area}
          onChange={(e) => handleInputChange('area', e.target.value)}
          className="form-input"
          placeholder="Enter watershed area in square kilometers"
          title="Please enter a valid number (e.g., 25.5)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={3}
          className="form-input"
          placeholder="Enter watershed description"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Watersheds</h1>
          <p className="text-sm md:text-base text-secondary-600">Manage watershed locations and their details</p>
        </div>
        {/* Hide watershed creation buttons for watershed_manager */}
        {user?.role !== 'watershed_manager' && (
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
            <button className="btn btn-secondary w-full md:w-auto" onClick={handleCSVUpload}>
              <Upload className="w-4 h-4 mr-2" />
              <span className="md:inline">Upload CSV</span>
            </button>
            <button className="btn btn-primary w-full md:w-auto" onClick={handleCreateFarm}>
              <Plus className="w-4 h-4 mr-2" />
              <span className="md:inline">Add Watershed</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
        {/* Filter Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
            <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
          </div>
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
          >
            <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
            {isFiltersCollapsed ? (
              <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
            ) : (
              <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
            )}
          </button>
        </div>

        {/* Collapsible Filter Content */}
        {!isFiltersCollapsed && (
          <div className="p-3 md:p-6">
            <div className="space-y-3 md:space-y-4">
              {/* Watershed Name Filter */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Filter by watershed name..."
                  value={filters.name}
                  onChange={(e) => handleNameFilter(e.target.value)}
                  className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                />
              </div>

              {/* Country and Owner Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Filter by country..."
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
                <input
                  type="text"
                  placeholder="Filter by owner..."
                  value={filters.owner}
                  onChange={(e) => handleFilterChange('owner', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
              </div>

              <div className="flex justify-center md:justify-end">
                {/* Clear Filters */}
                <button
                  onClick={() => setFilters({ name: '', country: '', owner: '', page: 1, limit: 20 })}
                  className="btn btn-secondary text-sm md:text-base px-4 py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Watersheds Table - Desktop */}
      <div className="hidden md:flex md:flex-col md:h-[calc(100vh-200px)] md:min-h-[500px]">
        <Table
          columns={columns}
          data={watersheds}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          emptyMessage="No watersheds found"
          className="flex-1 flex flex-col"
        />
      </div>

      {/* Watersheds Cards - Mobile */}
      <div className="md:hidden space-y-3 px-2">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : watersheds.length > 0 ? (
          watersheds.map((watershed) => (
            <div key={watershed.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between mb-3 w-full">
                <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                  <div className="p-1.5 rounded-md flex-shrink-0 bg-primary-100 text-primary-600">
                    <Building2 className="w-3 h-3" />
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {watershed.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate flex items-center">
                      <MapPin className="w-2 h-2 mr-1 flex-shrink-0" />
                      {watershed.address || watershed.country}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  <ActionButton
                    icon={Eye}
                    label="View Details"
                    onClick={() => handleViewFarm(watershed)}
                    size="sm"
                    className="p-1"
                  />
                  {user?.role !== 'watershed_manager' && (
                    <ActionMenu
                      item={watershed}
                      actions={[
                        {
                          key: 'report',
                          label: 'Submit Report',
                          icon: AlertTriangle,
                          variant: 'warning',
                          onClick: handleReportFarm,
                        },
                        {
                          key: 'edit',
                          label: 'Edit Watershed',
                          icon: Edit,
                          onClick: handleEditFarm,
                        },
                        {
                          key: 'delete',
                          label: 'Delete Watershed',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: handleDeleteFarm,
                        },
                      ]}
                    />
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs w-full">
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Location</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {watershed.country} {(watershed.state || watershed.region) && `, ${watershed.state || watershed.region}`}
                  </div>
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Area</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {watershed.area ? `${watershed.area} sq km` : 'Not specified'}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
            <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No watersheds found</p>
          </div>
        )}
      </div>

      {/* View Watershed Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Watershed Details"
        size="lg"
      >
        {selectedWatershed && (
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-primary-100 p-3 rounded-lg">
                <Building2 className="w-8 h-8 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{selectedWatershed.name}</h3>
                <p className="text-secondary-600 flex items-center mt-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  {selectedWatershed.address || selectedWatershed.country}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700">Watershed ID</label>
                <p className="mt-1 text-sm text-secondary-900 font-mono break-all">{selectedWatershed.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Country</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedWatershed.country || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">State</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedWatershed.state || selectedWatershed.region || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Area</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {selectedWatershed.area ? `${selectedWatershed.area} sq km` : 'Not specified'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created By</label>
                <p className="mt-1 text-secondary-900">{selectedWatershed.created_by || 'System'}</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-secondary-700">Address</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedWatershed.address || 'Not specified'}</p>
              </div>
              {selectedWatershed.coordinates && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700">Coordinates</label>
                  <div className="mt-1 text-sm text-secondary-900 font-mono">
                    <span>Latitude: {selectedWatershed.coordinates.latitude}</span>
                    <span className="mx-4">|</span>
                    <span>Longitude: {selectedWatershed.coordinates.longitude}</span>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-secondary-700">QR Code</label>
                {selectedWatershed.qr_code ? (
                  <a
                    href={selectedWatershed.qr_code}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    View QR Code
                  </a>
                ) : (
                  <div className="mt-1">
                    <p className="text-sm text-secondary-500 mb-2">No QR code generated</p>
                    <button
                      onClick={() => handleGenerateQRCode(selectedWatershed.id)}
                      disabled={qrCodeGenerating[selectedWatershed.id]}
                      className="btn btn-sm btn-primary disabled:opacity-50"
                    >
                      {qrCodeGenerating[selectedWatershed.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate QR Code
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedWatershed.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Last Updated</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedWatershed.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Watershed Modal */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleFormSubmit}
        title="Edit Watershed"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Create Watershed Modal */}
      <FormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleFormSubmit}
        title="Create New Watershed"
        loading={formLoading}
      >
        {renderFormFields()}
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Watershed"
        message={`Are you sure you want to delete "${selectedWatershed?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* CSV Upload Modal */}
      <Modal
        isOpen={showCSVUploadModal}
        onClose={() => setShowCSVUploadModal(false)}
        title="Upload Watersheds from CSV"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">CSV Format Requirements</h4>
            <p className="text-sm text-blue-700 mb-2">
              Your CSV file should contain the following columns (in any order):
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono text-blue-800">
              <div>• name</div>
              <div>• description</div>
              <div>• country</div>
              <div>• region</div>
              <div>• address</div>
              <div>• latitude</div>
              <div>• longitude</div>
              <div>• owner</div>
              <div>• contact</div>
              <div>• area</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Select CSV File
            </label>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              onChange={handleCSVFileChange}
              className="form-input file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {csvFile && (
              <p className="mt-2 text-sm text-green-600">
                Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowCSVUploadModal(false)}
              className="btn btn-secondary w-full sm:w-auto"
              disabled={csvUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCSVUploadSubmit}
              disabled={!csvFile || csvUploading}
              className="btn btn-primary w-full sm:w-auto"
            >
              {csvUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Submit Report"
        size="xl"
      >
        {reportWatershedData && (
          <ReportForm
            watershedData={reportWatershedData}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default WatershedsPage;